import React, { useState, useRef } from 'react';

const ImageUploader = ({ onImagesUploaded, onError }) => {
  const [selectedImages, setSelectedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    validateAndProcessFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files);
    validateAndProcessFiles(files);
  };

  const validateAndProcessFiles = (files) => {
    // 验证文件类型
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      onError('请仅上传图片文件');
      return;
    }

    // 最多允许上传5张图片
    if (validFiles.length > 10) {
      onError('最多只能上传10张图片');
      return;
    }

    setSelectedImages(validFiles);
  };

  const handleUpload = async () => {
    if (selectedImages.length === 0) {
      onError('请先选择图片');
      return;
    }

    try {
      setIsUploading(true);
      await onImagesUploaded(selectedImages);
      // 上传成功后清空已选图片
      setSelectedImages([]);
    } catch (error) {
      onError('上传失败: ' + error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newSelectedImages = [...selectedImages];
    newSelectedImages.splice(index, 1);
    setSelectedImages(newSelectedImages);
  };

  return (
    <div className="relative">
      {/* 上传按钮区域 */}
      <div className="space-y-4">
        {/* 拍照按钮 */}
        <button
          onClick={() => cameraInputRef.current.click()}
          className="w-full backdrop-blur-sm bg-gradient-to-r from-indigo-600 to-blue-500 text-white p-2 py-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-white/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="flex flex-col items-center justify-center space-y-1 relative z-10">
            <div className="flex flex-row items-center justify-center">
                <div className="flex rounded-full p-3 bg-white/20">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                </div>
                <p className="text-lg font-medium ml-2">拍照识别</p>
              </div>
            <div>
              <p className="text-sm opacity-80 mt-1">打开相机拍摄菜单</p>
            </div>
          </div>
        </button>

        {/* 相册上传按钮 */}
        <button
          onClick={() => galleryInputRef.current.click()}
          className="w-full backdrop-blur-sm bg-white/80 border border-indigo-100 py-3 px-4 rounded-xl hover:bg-indigo-50/50 transition-all duration-300 group"
        >
          <div className="flex items-center">
            <div className="rounded-full p-2 bg-indigo-100/80 group-hover:bg-indigo-200/80 transition-colors">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-left font-medium text-gray-700 group-hover:text-gray-900">从相册选择</p>
              <p className="text-xs text-left text-gray-500">上传已有的菜单照片</p>
            </div>
          </div>
        </button>

        {/* 隐藏的文件输入框 */}
        <input
          type="file"
          ref={cameraInputRef}
          className="hidden"
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
        />
        <input
          type="file"
          ref={galleryInputRef}
          className="hidden"
          onChange={handleFileChange}
          multiple
          accept="image/*"
        />
      </div>

      {/* 预览已选图片 */}
      {selectedImages.length > 0 && (
        <div className="mt-2 backdrop-blur-sm bg-white/60 rounded-xl py-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">已选择 {selectedImages.length} 张图片</h3>
          <div className="grid grid-cols-3 gap-3">
            {selectedImages.map((image, index) => (
              <div key={index} className="relative group">
                <div className="aspect-w-3 aspect-h-3 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`菜单图片 ${index + 1}`}
                    className="object-cover w-full h-full"
                  />
                </div>
                <button
                  type="button"
                  className="absolute top-1 right-1 bg-black/50 backdrop-blur-sm rounded-full p-1.5 text-white hover:bg-red-500/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 开始识别按钮 */}
      {selectedImages.length > 0 && (
        <button
          onClick={handleUpload}
          disabled={isUploading}
          className="w-full mt-2 backdrop-blur-sm bg-gradient-to-r from-indigo-600 to-blue-500 text-white py-4 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 disabled:opacity-70 flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-lg">开始识别菜单</span>
        </button>
      )}
    </div>
  );
};

export default ImageUploader; 